import { type FormEvent, useEffect, useState } from "react";
import { fetchSalesProfile, updateSalesProfile, type SalesProfile } from "../../lib/sales-profile";

type EditorState = {
  firstName: string;
  middleName: string;
  lastName: string;
  email: string;
  phone: string;
};

const emptyEditorState: EditorState = {
  firstName: "",
  middleName: "",
  lastName: "",
  email: "",
  phone: ""
};

function toEditorState(profile: SalesProfile): EditorState {
  return {
    firstName: profile.firstName,
    middleName: profile.middleName,
    lastName: profile.lastName,
    email: profile.email,
    phone: profile.phone
  };
}

export function SalesProfilePage() {
  const [profile, setProfile] = useState<SalesProfile | null>(null);
  const [editor, setEditor] = useState<EditorState>(emptyEditorState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    let ignore = false;

    async function load() {
      setLoading(true);
      setError(null);

      try {
        const result = await fetchSalesProfile();
        if (!ignore) {
          setProfile(result);
          setEditor(toEditorState(result));
        }
      } catch (loadError) {
        if (!ignore) {
          setError(loadError instanceof Error ? loadError.message : "Unable to load profile.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    }

    void load();
    return () => {
      ignore = true;
    };
  }, []);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();

    if (!editor.firstName.trim()) {
      setError("First name is required.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateSalesProfile({
        firstName: editor.firstName,
        middleName: editor.middleName,
        lastName: editor.lastName,
        phone: editor.phone
      });
      const refreshed = await fetchSalesProfile();
      setProfile(refreshed);
      setEditor(toEditorState(refreshed));
      setSuccessMessage("Profile updated successfully.");
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : "Unable to update profile.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6 pb-6">
      <div>
        <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Sales profile</p>
        <h1 className="mt-3 text-4xl font-semibold tracking-tight text-nv-ink">Your account</h1>
        <p className="mt-4 max-w-3xl text-nv-slate">
          Keep your name and phone number current for sales desk accountability. Email stays fixed.
        </p>
      </div>

      {error ? <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-300">{error}</div> : null}
      {successMessage ? <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-700 dark:border-emerald-900/70 dark:bg-emerald-950/40 dark:text-emerald-300">{successMessage}</div> : null}

      <div className="grid gap-4 xl:grid-cols-[minmax(0,0.95fr)_minmax(360px,1.05fr)]">
        <div className="admin-card rounded-[32px] p-6">
          {loading ? (
            <div className="text-sm text-nv-slate">Loading profile...</div>
          ) : profile ? (
            <div className="space-y-5">
              <div className="rounded-[28px] bg-[linear-gradient(135deg,rgba(8,145,178,0.14),rgba(255,255,255,0.55))] p-5 dark:bg-[linear-gradient(135deg,rgba(8,145,178,0.18),rgba(255,255,255,0.03))]">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Signed in as</p>
                <h2 className="mt-3 text-3xl font-semibold tracking-tight text-nv-ink">
                  {[profile.firstName, profile.lastName].filter(Boolean).join(" ") || profile.email}
                </h2>
                <p className="mt-2 text-sm text-nv-slate">{profile.role}</p>
              </div>

              <div className="field-soft p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Email</p>
                <p className="mt-2 text-sm font-semibold text-nv-ink">{profile.email || "Not recorded"}</p>
                <p className="mt-2 text-sm text-nv-slate">Email is locked and cannot be changed from the sales account.</p>
              </div>

              <div className="field-soft p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Phone</p>
                <p className="mt-2 text-sm font-semibold text-nv-ink">{profile.phone || "Not recorded"}</p>
              </div>
            </div>
          ) : (
            <div className="text-sm text-nv-slate">Profile unavailable.</div>
          )}
        </div>

        <div className="admin-card rounded-[32px] p-6">
          <p className="text-sm uppercase tracking-[0.2em] text-cyan-300">Edit profile</p>
          <h2 className="mt-2 text-2xl font-semibold tracking-tight text-nv-ink">Allowed changes</h2>

          <form className="mt-6 space-y-5" onSubmit={(event) => void handleSubmit(event)}>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">First name</label>
                <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, firstName: event.target.value }))} value={editor.firstName} />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Last name</label>
                <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, lastName: event.target.value }))} value={editor.lastName} />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Middle name</label>
                <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, middleName: event.target.value }))} value={editor.middleName} />
              </div>
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Phone</label>
                <input className="field-control px-4 py-3 text-sm" onChange={(event) => setEditor((current) => ({ ...current, phone: event.target.value }))} value={editor.phone} />
              </div>
              <div className="md:col-span-2">
                <label className="mb-2 block text-xs font-semibold uppercase tracking-[0.2em] text-nv-slate">Email</label>
                <input className="field-control cursor-not-allowed px-4 py-3 text-sm opacity-70" disabled value={editor.email} />
              </div>
            </div>

            <div className="rounded-[24px] border border-dashed border-black/10 p-4 text-sm text-nv-slate dark:border-white/10">
              You can update your name and phone number here. Email changes remain restricted.
            </div>

            <button className="brand-button rounded-full px-5 py-3 text-sm font-semibold text-white disabled:opacity-60" disabled={saving || loading} type="submit">
              {saving ? "Saving..." : "Save profile"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
